<template>
  <v-container class="user-edit py-4 py-sm-6" fluid>
    <v-card outlined class="rounded-xl mx-auto user-edit__card">
      <v-card-title class="align-start pa-5 pa-sm-6">
        <v-avatar color="primary" class="mr-4" size="44">
          <v-icon dark>mdi-account-cog</v-icon>
        </v-avatar>
        <div>
          <div class="headline">{{ $t("pageHeader.updateUser") }}</div>
          <div class="body-2 font-weight-regular text--secondary mt-1">
            Update profile details and account security.
          </div>
        </div>
      </v-card-title>

      <v-divider />

      <v-card-text class="pa-5 pa-sm-6">
        <v-skeleton-loader v-if="loading" type="heading, paragraph, actions" />

        <v-form v-else @submit.prevent="submit">
          <div class="subtitle-1 font-weight-medium mb-1">Profile details</div>
          <div class="body-2 text--secondary mb-4">
            Keep the contact information for this user up to date.
          </div>

          <v-row>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model.trim="user.firstName"
                outlined
                dense
                :error-messages="nameErrors"
                :label="$t('labels.firstName')"
                autocomplete="given-name"
                @blur="$v.user.firstName.$touch()"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model.trim="user.lastName"
                outlined
                dense
                :label="$t('labels.lastName')"
                autocomplete="family-name"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model.trim="user.phoneNumber"
                outlined
                dense
                type="tel"
                :error-messages="phoneNumberErrors"
                :label="$t('labels.phoneNumber')"
                autocomplete="tel"
                @blur="$v.user.phoneNumber.$touch()"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model.trim="user.email"
                outlined
                dense
                type="email"
                :label="$t('labels.emailAddress')"
                autocomplete="email"
                hint="Your sign-in email cannot be changed here."
                persistent-hint
                readonly
              />
            </v-col>
          </v-row>

          <v-divider v-if="canChangePassword" class="my-5" />

          <div
            v-if="canChangePassword"
            class="d-flex align-start justify-space-between flex-wrap"
          >
            <div class="pr-4 mb-2">
              <div class="subtitle-1 font-weight-medium">Password</div>
              <div class="body-2 text--secondary">
                Change the password only when this user requests it.
              </div>
            </div>
            <v-switch
              v-model="changePassword"
              color="primary"
              class="mt-0"
              label="Change password"
              hide-details
              @change="clearPasswordFields"
            />
          </div>

          <v-expand-transition>
            <v-row v-if="canChangePassword && changePassword" class="mt-3">
              <v-col cols="12">
                <v-alert text dense type="info" class="mb-0">
                  This changes only the sign-in password. Organization access is
                  managed separately from the Users settings page.
                </v-alert>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="password.current"
                  outlined
                  dense
                  :type="showCurrentPassword ? 'text' : 'password'"
                  :append-icon="showCurrentPassword ? 'mdi-eye-off' : 'mdi-eye'"
                  label="Current password"
                  autocomplete="current-password"
                  :error-messages="currentPasswordErrors"
                  @blur="$v.password.current.$touch()"
                  @click:append="showCurrentPassword = !showCurrentPassword"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="password.next"
                  outlined
                  dense
                  :type="showNewPassword ? 'text' : 'password'"
                  :append-icon="showNewPassword ? 'mdi-eye-off' : 'mdi-eye'"
                  label="New password"
                  autocomplete="new-password"
                  :error-messages="newPasswordErrors"
                  hint="Use at least 8 characters."
                  persistent-hint
                  @blur="$v.password.next.$touch()"
                  @click:append="showNewPassword = !showNewPassword"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="password.confirm"
                  outlined
                  dense
                  :type="showConfirmPassword ? 'text' : 'password'"
                  :append-icon="showConfirmPassword ? 'mdi-eye-off' : 'mdi-eye'"
                  label="Confirm new password"
                  autocomplete="new-password"
                  :error-messages="confirmPasswordErrors"
                  @blur="$v.password.confirm.$touch()"
                  @click:append="showConfirmPassword = !showConfirmPassword"
                />
              </v-col>
            </v-row>
          </v-expand-transition>
        </v-form>
      </v-card-text>

      <v-divider />
      <v-card-actions class="pa-4 pa-sm-5 user-edit__actions">
        <v-spacer class="hidden-xs-only" />
        <v-btn text :disabled="saving" @click="cancel">Cancel</v-btn>
        <v-btn color="primary" :loading="saving" :disabled="loading" @click="submit">
          {{ $t("buttons.update") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-container>
</template>

<script>
import userService from "../service";
import { mapGetters } from "vuex";
import { validationMixin } from "vuelidate";
import { required, requiredIf, minLength, email, sameAs } from "vuelidate/lib/validators";

export default {
  name: "user-edit",
  mixins: [validationMixin],
  props: {
    isModal: Boolean,
  },
  data() {
    return {
      loading: true,
      saving: false,
      changePassword: false,
      showCurrentPassword: false,
      showNewPassword: false,
      showConfirmPassword: false,
      user: {
        id: null,
        firstName: "",
        lastName: "",
        email: "",
        phoneNumber: "",
      },
      password: {
        current: "",
        next: "",
        confirm: "",
      },
    };
  },
  computed: {
    ...mapGetters("global", ["currentUser"]),
    canChangePassword() {
      return Number(this.currentUser && this.currentUser.id) === Number(this.user.id);
    },
    nameErrors() {
      if (!this.$v.user.firstName.$dirty) return [];
      if (!this.$v.user.firstName.required) return ["Field is required"];
      if (!this.$v.user.firstName.minLength)
        return [this.$t("validations.fieldMustbeatLeast4ChrLong")];
      return [];
    },
    phoneNumberErrors() {
      if (!this.$v.user.phoneNumber.$dirty) return [];
      return this.$v.user.phoneNumber.minLength
        ? []
        : [this.$t("validations.validLetters10")];
    },
    currentPasswordErrors() {
      if (!this.$v.password.current.$dirty) return [];
      return this.$v.password.current.required ? [] : ["Current password is required"];
    },
    newPasswordErrors() {
      if (!this.$v.password.next.$dirty) return [];
      if (!this.$v.password.next.required) return ["New password is required"];
      return this.$v.password.next.minLength ? [] : ["Use at least 8 characters"];
    },
    confirmPasswordErrors() {
      if (!this.$v.password.confirm.$dirty) return [];
      if (!this.$v.password.confirm.required) return ["Confirm the new password"];
      return this.$v.password.confirm.sameAsPassword ? [] : ["Passwords do not match"];
    },
  },
  created() {
    this.loadUser();
  },
  methods: {
    async loadUser() {
      if (!this.$route.params || !this.$route.params.id) {
        this.close();
        return;
      }

      this.$Progress.start();
      try {
        const response = await userService.getById(this.$route.params.id);
        const user = response.data;
        this.user = {
          id: user.id,
          firstName: user.firstName || "",
          lastName: user.lastName || "",
          email: user.email || "",
          phoneNumber: user.phoneNumber || "",
        };
        this.$Progress.finish();
      } catch (error) {
        this.$Progress.fail();
        this.showToast("error", this.getErrorMessage(error, "Unable to load user."));
      } finally {
        this.loading = false;
      }
    },
    clearPasswordFields(enabled) {
      if (enabled) return;
      this.password = { current: "", next: "", confirm: "" };
      this.$v.password.$reset();
    },
    async submit() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        this.showToast("error", "Please correct the highlighted fields.");
        return;
      }

      if (this.canChangePassword && this.changePassword) {
        const confirmation = await this.$swal.fire({
          title: "Change password?",
          text: "This changes the sign-in password only. The user may need to sign in again with the new password.",
          type: "warning",
          showCancelButton: true,
          confirmButtonText: "Change password",
          cancelButtonText: "Cancel",
          confirmButtonColor: this.$vuetify.theme.currentTheme.primary,
        });
        if (!confirmation.value) return;
      }

      const payload = { ...this.user };
      if (this.canChangePassword && this.changePassword) {
        payload.currentPassword = this.password.current;
        payload.password = this.password.next;
      }

      this.saving = true;
      this.$Progress.start();
      try {
        await userService.update(this.user.id, payload);
        this.$Progress.finish();
        this.showToast("success", this.$t("toastMessage.text.userUpdate"));
        this.close();
      } catch (error) {
        this.$Progress.fail();
        this.showToast("error", this.getErrorMessage(error, "Unable to update user."));
      } finally {
        this.saving = false;
      }
    },
    showToast(type, title) {
      this.$swal.mixin({
        toast: true,
        showConfirmButton: true,
        position: "top-end",
        timer: 3000,
      }).fire({ type, title });
    },
    getErrorMessage(error, fallback) {
      return (error && error.data && (error.data.message || error.data)) ||
        (error && error.response && error.response.data && error.response.data.message) ||
        fallback;
    },
    cancel() {
      this.close();
    },
    close() {
      if (this.isModal) this.$emit("close");
      else this.$router.go(-1);
    },
  },
  validations: {
    user: {
      firstName: { required, minLength: minLength(4) },
      email: { email },
      phoneNumber: { minLength: minLength(10) },
    },
    password: {
      current: { required: requiredIf(function () { return this.changePassword; }) },
      next: {
        required: requiredIf(function () { return this.changePassword; }),
        minLength: minLength(8),
      },
      confirm: {
        required: requiredIf(function () { return this.changePassword; }),
        sameAsPassword: sameAs("next"),
      },
    },
  },
};
</script>

<style scoped>
.user-edit__card {
  max-width: 920px;
}

@media (max-width: 599px) {
  .user-edit__actions {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
  }

  .user-edit__actions .v-btn {
    margin: 0;
  }
}
</style>
